import type { ReactElement } from 'react';
import { useEffect, useRef } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';

import { StatusBanner } from '@xing-com/banner';
import { Button } from '@xing-com/button';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { useJobsNwtTracking } from '@xing-com/crate-jobs-tracking';
import { Divider } from '@xing-com/divider';
import { IconEdit, IconRocket } from '@xing-com/icons';
import { BodyCopy } from '@xing-com/typography';

import { LegalInfo } from '../../legal-info';
import type { ExternalDocsLegacySupportProp, Step } from '../types';

import {
  CTAButton,
  CTAWrapper,
  Container,
  LegalInfoWrapper,
  StepItem,
  StepList,
  StepTick,
  StepTitle,
  Title,
} from './wizard-review.styles';

type Props = {
  steps: Step[];
  reviewStepContent?: ReactElement;
  isLoading: boolean;
  onReviewStep: (step: number) => void;
  onSubmit: () => void;
  trackingProps: {
    itemUrn: string;
    flags: string;
    page: string;
    context: string;
  };
  externalDocuments?: ExternalDocsLegacySupportProp;
};

export const WizardReview: React.FC<Props> = ({
  steps,
  reviewStepContent,
  isLoading,
  onReviewStep,
  onSubmit,
  trackingProps,
  externalDocuments,
}) => {
  const { isLoggedIn } = useLoginState();
  const trackNwt = useJobsNwtTracking();
  const { formatMessage } = useIntl();
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (headingRef.current) {
      headingRef.current.focus();
    }
  }, []);

  useEffect(() => {
    trackNwt({
      eventSchema: 'basic',
      event: 'viewed_screen',
      ...trackingProps,
      page: 'jobs/apply/review',
    });
  }, [trackNwt, trackingProps]);

  return (
    <Container>
      <Title forwardedAs="h1" ref={headingRef} tabIndex={-1}>
        <FormattedMessage id="JOBS_APPLY_WIZARD_REVIEW_HEADLINE" />
      </Title>
      <StepList>
        {steps.map((step, index) => (
          <StepItem key={`${index}-${step.name}`}>
            <StepTitle>
              <StepTick /> <FormattedMessage id={step.name} />
            </StepTitle>
            <Button
              size="small"
              icon={IconEdit}
              variant="tertiary"
              disabled={isLoading}
              onClick={() => onReviewStep(index + 1)}
              aria-label={`${formatMessage({ id: step.name })}: ${formatMessage(
                {
                  id: 'JOBS_APPLY_WIZARD_REVIEW_EDIT_BUTTON_LABEL',
                }
              )}`}
            />
          </StepItem>
        ))}
      </StepList>
      {isLoggedIn ? (
        <StatusBanner variant="info" display="inline">
          <FormattedMessage id="JOBS_APPLY_WIZARD_REVIEW_STORE_INFO_BANNER" />
        </StatusBanner>
      ) : null}
      {reviewStepContent}
      <Divider />
      <LegalInfoWrapper>
        <LegalInfo externalDocuments={externalDocuments} />
        {!isLoggedIn ? (
          <BodyCopy>
            <FormattedMessage id="JOBS_INSTANT_APPLY_CREATE_PROFILE_CONSENT_WITH_CV_DATA" />
          </BodyCopy>
        ) : null}
      </LegalInfoWrapper>
      <CTAWrapper>
        <CTAButton icon={IconRocket} onClick={onSubmit} loading={isLoading}>
          <FormattedMessage id="JOBS_APPLY_WIZARD_BUTTON_SUBMIT" />
        </CTAButton>
      </CTAWrapper>
    </Container>
  );
};
